import {get} from 'object-path-immutable'
import React, {useEffect, useMemo, useState} from 'react'
import {Helmet} from 'react-helmet-async'
import {PreviewFlagMarker} from '../../../components/feature-flags'
import {FormSpacer} from '../../../components/form'
import {Button} from '../../../components/library/buttons'
import {
  DropdownBody,
  DropdownFooter,
  DropdownItem
} from '../../../components/library/dropdown/content'
import {TextInput} from '../../../components/library/form'
import {Icon} from '../../../components/library/icon/icon'
import {
  List,
  ListColumns,
  NoResults,
  NoResultsPermissions
} from '../../../components/library/list'
import {Tag} from '../../../components/library/tag/tag'
import {FormModal} from '../../../components/modals'
import {OverflowFooter} from '../../../components/overflow'
import {
  IntegrationType,
  PermissionAction,
  PermissionResource
} from '../../../data/workbench-api/generated/types'
import {EnvironmentNode} from '../../../environment'
import {useStateChanged} from '../../../hooks/use-state-changed'
import {useAnalytics} from '../../../lib/analytics/analytics'
import {useToast} from '../../../lib/toast'
import {IconHeading, Section, SectionContent, SectionInfo} from '../components'
import {ConfirmIntegrationActionModal} from './modals/confirm'
import {
  IntegrationDetailFragment,
  useAddIntegrationMutation,
  useDeleteIntegrationMutation,
  useGetIntegrationsQuery,
  useUpdateIntegrationMutation
} from './generated'

export const Salesforce = ({environment}: {environment: EnvironmentNode}) => {
  const toast = useToast()
  const analytics = useAnalytics()

  const canView = environment.hasPermission(
    PermissionResource.Environment,
    PermissionAction.View
  )
  const canEdit = environment.hasPermission(
    PermissionResource.Environment,
    PermissionAction.Admin
  )

  const [update] = useUpdateIntegrationMutation()
  const [deleteMutation] = useDeleteIntegrationMutation()
  const {data, loading, refetch} = useGetIntegrationsQuery({
    variables: {
      environmentId: environment.id,
      integrationType: IntegrationType.SfmcJb
    }
  })
  const integrations = data?.integrationConfigurations?.nodes ?? []

  useEffect(() => {
    analytics.track({
      event: 'Viewed salesforce configuration',
      properties: {
        org_id: environment.organisationId,
        environment_id: environment.id,
        category: 'configuration>card-templates>card-actions'
      }
    })
  }, [analytics, environment])

  const [add, setAdd] = useState(false)
  const [edit, setEdit] = useState<IntegrationDetailFragment>()
  const [confirm, setConfirm] = useState<
    {action: 'disable' | 'delete'; item: IntegrationDetailFragment} | undefined
  >()

  const title = 'Salesforce Marketing Cloud Journey Builder integration'

  const columns: ListColumns<IntegrationDetailFragment> = useMemo(
    () => [
      {
        id: 'name',
        header: 'Name',
        accessor: r => <a onClick={() => setEdit(r)}>{r.name}</a>
      },
      {
        id: 'linked',
        header: 'Linked card templates',
        accessor: r => r.associatedActionFlows?.length ?? 0
      },
      {
        id: 'enabled',
        header: 'Status',
        accessor: r =>
          r.enabled ? (
            <Tag appearance="green">Enabled</Tag>
          ) : (
            <Tag appearance="light">Disabled</Tag>
          )
      }
    ],
    []
  )

  return (
    <>
      <Section>
        <Helmet>
          <title>{title}</title>
        </Helmet>
        <header>
          <IconHeading>
            <Icon name="building" /> {title}
            <PreviewFlagMarker />
          </IconHeading>

          <nav>
            <Button
              disabled={!canEdit}
              onClick={() => {
                setAdd(true)
              }}
            >
              Add integration
            </Button>
          </nav>
        </header>

        <SectionInfo>
          <p>
            To enable integrations with Salesforce Marketing Cloud, enter the
            JWT signing secret available from the Installed Packages screen once
            you have installed the Atomic plugin.
          </p>
        </SectionInfo>

        <SectionContent>
          <List
            columns={columns}
            rows={integrations}
            renderOverflow={(item, closeDropdown) => (
              <Overflow
                item={item}
                closeDropdown={closeDropdown}
                setEdit={setEdit}
                refetch={refetch}
                canEdit={canEdit}
                enable={integrationId => {
                  update({
                    variables: {
                      environmentId: environment.id,
                      id: integrationId,
                      enabled: true
                    }
                  })
                }}
                setDelete={integration => {
                  setConfirm({action: 'delete', item: integration})
                }}
                setDisable={integration => {
                  setConfirm({action: 'disable', item: integration})
                }}
              />
            )}
            loading={loading}
            renderEmpty={() =>
              canView ? (
                <NoResults
                  icon="building"
                  heading="No integrations"
                  message="No integrations configured"
                />
              ) : (
                <NoResultsPermissions />
              )
            }
          />
        </SectionContent>
      </Section>
      {add ? (
        <AddModal
          organisationId={environment.organisationId}
          environmentId={environment.id}
          handleClose={() => {
            setAdd(false)
          }}
          canEdit={canEdit}
          onCompleted={() => {
            analytics.track({
              event: 'Added configuration',
              properties: {
                org_id: environment.organisationId,
                environment_id: environment.id,
                category:
                  'configuration>salesforce>marketing-cloud-journey-builder'
              }
            })
            setAdd(false)
            toast('Integration added')
            refetch()
          }}
        />
      ) : null}
      {edit ? (
        <EditModal
          integration={edit}
          handleClose={() => {
            setEdit(undefined)
          }}
          canEdit={canEdit}
          onCompleted={() => {
            analytics.track({
              event: 'Edited configuration',
              properties: {
                org_id: environment.organisationId,
                environment_id: environment.id,
                category:
                  'configuration>salesforce>marketing-cloud-journey-builder'
              }
            })
            setEdit(undefined)
            toast('Credential updated')
            refetch()
          }}
        />
      ) : null}
      {confirm ? (
        <ConfirmIntegrationActionModal
          integration={confirm.item}
          mode={confirm.action}
          onClose={() => {
            setConfirm(undefined)
          }}
          onConfirm={() => {
            if (confirm.action === 'disable') {
              update({
                variables: {
                  environmentId: confirm.item.environmentId,
                  id: confirm.item.id,
                  enabled: false
                }
              })
                .then(() => {
                  toast('Integration disabled', 'success')
                })
                .catch(e => {
                  toast('Failed to disable integration', 'error')
                  throw e
                })
            } else if (confirm.action === 'delete') {
              deleteMutation({
                variables: {
                  environmentId: confirm.item.environmentId,
                  id: confirm.item.id
                }
              })
                .catch(e => {
                  toast('Failed to delete integration', 'error')
                  throw e
                })
                .then(() => {
                  toast('Integration deleted', 'success')
                  refetch()
                })
            }
            setConfirm(undefined)
          }}
        />
      ) : null}
    </>
  )
}

type OverflowProps = {
  item: IntegrationDetailFragment
  closeDropdown: () => void
  setEdit: (obj: IntegrationDetailFragment) => void
  setDisable: (obj: IntegrationDetailFragment) => void
  setDelete: (obj: IntegrationDetailFragment) => void
  enable: (integrationId: string) => void
  refetch: () => void
  canEdit: boolean
}
const Overflow = ({
  item,
  closeDropdown,
  setEdit,
  setDisable,
  setDelete,
  enable,
  canEdit
}: OverflowProps) => {
  return (
    <>
      <DropdownBody>
        <DropdownItem>
          <a
            onClick={() => {
              setEdit(item)
              closeDropdown()
            }}
          >
            {canEdit ? 'Edit' : 'View details'}
          </a>
        </DropdownItem>

        {canEdit ? (
          <DropdownItem>
            {item.enabled ? (
              <a
                onClick={() => {
                  setDisable(item)
                  closeDropdown()
                }}
              >
                Disable
              </a>
            ) : (
              <a
                onClick={() => {
                  enable(item.id)
                  closeDropdown()
                }}
              >
                Enable
              </a>
            )}
          </DropdownItem>
        ) : null}

        {canEdit && !item.enabled ? (
          <DropdownItem>
            <a
              onClick={() => {
                setDelete(item)
                closeDropdown()
              }}
            >
              Delete
            </a>
          </DropdownItem>
        ) : null}
      </DropdownBody>
      <DropdownFooter>
        <OverflowFooter id={item.id} created={item.created} />
      </DropdownFooter>
    </>
  )
}

type FormFields = Pick<IntegrationDetailFragment, 'name' | 'configuration'>

interface JourneyBuilderIntegrationFormProps {
  integration: FormFields
  update: (updater: (obj: FormFields) => FormFields) => void
  canEdit: boolean
}
export const JourneyBuilderIntegrationForm = ({
  integration,
  update,
  canEdit
}: JourneyBuilderIntegrationFormProps) => {
  return (
    <>
      <TextInput
        upLabel="Name"
        label="Enter a name for this integration..."
        value={integration.name || ''}
        onChange={name => update(c => ({...c, name}))}
        readOnly={!canEdit}
      />
      <FormSpacer />
      <TextInput
        label="Enter signing secret"
        upLabel="Signing secret"
        value={get(integration.configuration, 'signingSecret') ?? ''}
        onChange={signingSecret =>
          update(c => ({
            ...c,
            configuration: {
              ...(c.configuration as object),
              signingSecret
            }
          }))
        }
        readOnly={!canEdit}
      />
    </>
  )
}

const integrationIsValid = (integration: FormFields) =>
  !!integration.name &&
  !!get(integration.configuration, 'signingSecret') &&
  !get(integration.configuration, 'signingSecret').match(/(^\s+|\s+$)/)

interface AddModalProps {
  organisationId: string
  environmentId: string
  handleClose(): void
  onCompleted: () => void
  canEdit: boolean
}
export const AddModal = ({
  environmentId,
  handleClose,
  onCompleted,
  canEdit
}: AddModalProps) => {
  const [localObj, setLocalObj, dirty] = useStateChanged<FormFields>({
    name: '',
    configuration: {
      signingSecret: ''
    }
  })
  const [doMutation, {loading, error}] = useAddIntegrationMutation({
    onCompleted
  })

  return (
    <FormModal
      title="Add integration"
      handleClose={handleClose}
      onSubmit={() =>
        doMutation({
          variables: {
            environmentId,
            integrationType: IntegrationType.SfmcJb,
            ...localObj
          }
        })
      }
      error={error}
      isValid={dirty && !loading && integrationIsValid(localObj)}
    >
      <JourneyBuilderIntegrationForm
        integration={localObj}
        update={setLocalObj}
        canEdit={canEdit}
      />
    </FormModal>
  )
}

interface EditModalProps {
  integration: IntegrationDetailFragment
  handleClose: () => void
  onCompleted: () => void
  canEdit: boolean
}

export const EditModal = ({
  integration,
  handleClose,
  onCompleted,
  canEdit
}: EditModalProps) => {
  const [localObj, setLocalObj, dirty] = useStateChanged<FormFields>({
    name: integration.name,
    configuration: integration.configuration
  })
  const [doMutation, {loading, error}] = useUpdateIntegrationMutation({
    onCompleted
  })

  return (
    <FormModal
      readOnly={!canEdit}
      title={canEdit ? 'Edit integration' : 'View integration'}
      handleClose={handleClose}
      onSubmit={() => {
        return doMutation({
          variables: {
            environmentId: integration.environmentId,
            id: integration.id,
            ...localObj
          }
        })
      }}
      error={error}
      isValid={dirty && !loading && integrationIsValid(localObj)}
    >
      <JourneyBuilderIntegrationForm
        integration={localObj}
        update={setLocalObj}
        canEdit={canEdit}
      />
    </FormModal>
  )
}
